import type { AwardCheck, AwardLetterLine, AwardLetters, Me } from '@myrewards/contracts';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, RadioButton, RadioField, RadioGroup } from 'react-aria-components';
import { useParams } from 'react-router';
import { meQuery } from '../../api';
import {
  generateLetters,
  letterFileHref,
  letterInlineHref,
  letterKeys,
  lettersQuery,
  releaseLetters,
} from '../../api/letters';
import { checkQuery, queueQuery } from '../../api/ops';
import { settingQuery } from '../../api/settings';
import { Hero, PageBody, TwoColumns } from '../../shell';
import {
  Button,
  ButtonLink,
  Card,
  ConfirmDialog,
  EmptyState,
  formatDate,
  formatShortDate,
  Icon,
  plural,
  scoped,
  Select,
  StatusPill,
  TextField,
  useToast,
} from '../../ui';
import { PdfPreview } from '../../ui/PdfPreview';
import { QUEUE_VIEW, SCAN, TYPE_LABEL, TYPE_SHORT, amountWords } from '../labels';
import { PersonPicker } from '../../settings/PersonPicker';
import { showsEmptyOptions, TemplateOptionsNote } from '../../settings/TemplateNote';
import { SettingChange } from '../SettingChange';
import { BackLink, Notice } from '../shared';
import { ClaimButton, useClaimChanged } from '../queue/ClaimButton';
import { rowHref } from '../queue/presenter';
import {
  awardIssueCodes,
  automaticIssues,
  automaticNotice,
  changedHolders,
  replacedHolders,
  defaultHolderWords,
  firstName,
  builtInSampleName,
  builtInSampleNotice,
  generatedWords,
  heldIssues,
  isBuiltInSample,
  holderChoices,
  holderOf,
  letterFacts,
  letterProblemWords,
  lettersTitle,
  issueWords,
  lineNotes,
  mergedIssues,
  personStateOf,
  reviewWords,
  lineProblemItems,
  TAX_TO_ENTER_LINK,
  taxMissingWords,
  mergeSummary,
  nextInQueue,
  onlyAwardIssues,
  recipientWords,
  refusedIssues,
  releaseBatch,
  releaseBlockers,
  releasedWords,
  followingWords,
  remakeable,
  remakeLabel,
  releaseLabel,
  releaseSentence,
  releaseSummary,
  shownVersion,
  staleWords,
  taxRows,
  TEMPLATES_HREF,
  templateOptions,
  type HolderChoice,
} from './presenter';
import { LetterWordingCard } from './LetterWordingCard';
import { PublicCitation } from './PublicCitation';
import { GapCard } from './GapCard';
import { HeldClaim } from './HeldClaim';
import { CheckAgainstAward, ReviewPanel, StateChip } from './LetterReview';
import lt from './Letters.module.css';
import o from '../Ops.module.css';
import s from '../Steps.module.css';
import { ManageTabs } from '../../manage/ManageTabs';

type Line = AwardLetterLine;

/** Poll while a letter waits for its virus check: it's usually through in under a minute. */
const CHECKING_POLL_MS = 4_000;

function useAwardId(): string {
  const { id } = useParams();
  if (id === undefined) throw new Error('The letters route needs an award id.');
  return id;
}

/** Who holds the claim, and what that means for this screen. */
function ClaimState({
  check,
  letters,
  me,
}: {
  check: AwardCheck;
  letters: AwardLetters;
  me: Me;
}): ReactElement | null {
  if (letters.released) return null;
  const holder = check.claimedBy;
  if (holder !== null && holder.id === me.person.id) return null;
  const displayId = letters.displayId ?? 'this award';
  if (holder !== null)
    return (
      <HeldClaim
        awardId={letters.requestId}
        check={check}
        displayId={displayId}
        holder={holder}
        me={me}
      />
    );
  return (
    <div className={s.claimRow}>
      <Notice tone="info">
        Nobody holds {displayId} right now. Claim it to make and release its letters; your claim
        shows to the whole team.
      </Notice>
      {check.actions.claim && (
        <ClaimButton
          awardId={letters.requestId}
          displayId={displayId}
          label="Claim to prepare letters"
          version={check.award.version}
          to={`/queue/${letters.requestId}/letters`}
        />
      )}
    </div>
  );
}

/**
 * What every letter is made from: the template and the award date, side by side, then Generate
 * (QA-050). Generate waits for the date, since every letter prints it; a changed date makes every
 * letter again. What the whole award needs (the date, the signer) is said here once.
 */
function TemplateCard({
  letters,
  templateId,
  onTemplate,
  overrideReason,
  onOverrideReason,
  targetLine,
  onTargetLine,
  dateDraft,
  onDate,
  yours,
  onGenerate,
  generating,
  error,
  awardIssues,
}: {
  letters: AwardLetters;
  templateId: string | null;
  onTemplate: (id: string | null) => void;
  overrideReason: string;
  onOverrideReason: (reason: string) => void;
  targetLine: string | null;
  onTargetLine: (id: string | null) => void;
  dateDraft: string;
  onDate: (date: string) => void;
  yours: boolean;
  onGenerate: () => void;
  generating: boolean;
  error: string | null;
  /** Issue codes about the whole award, from the view and from a refused call. */
  awardIssues: readonly string[];
}): ReactElement {
  const options = [
    { id: 'automatic', label: 'Automatic mapping · each person’s signer and amounts' },
    ...templateOptions(letters, targetLine),
  ];
  const chosen =
    templateId === null
      ? (letters.lines.find((line) => line.selectedTemplate)?.selectedTemplate ??
        letters.templates.default ??
        undefined)
      : letters.templates.available.find((t) => t.id === templateId);
  const made = generatedWords(letters);
  const anyMade = letters.lines.some((l) => l.letter !== null);
  const noDate = dateDraft === '';
  const dateChanged = !noDate && dateDraft !== letters.awardDate;
  const canGenerate = letters.actions.generate && !letters.released;
  const canOverride = letters.actions.overrideTemplate !== false;
  const signerWords = awardIssues
    .filter((code) => code !== 'award_date_required')
    .map((code) => issueWords(code, ''));
  // APP-212: while a letter still to go is the built-in sample, the step says so.
  const sampleNotice = builtInSampleNotice(letters);
  const help = {
    shows: `The ${TYPE_LABEL[letters.type]} letter templates you can use, the award date every letter prints, and when these letters were last made.`,
    canDo:
      'Automatic mapping selects each person’s approved template. An alternate template needs a reason. Set the award date, generate, and review the actual PDF before release.',
  };
  return (
    <Card title="Template" help={help}>
      {letters.templates.available.length === 0 &&
      !letters.lines.some((l) => l.selectedTemplate) ? (
        <EmptyState
          variant="not-set"
          art="letter"
          size="sm"
          headline={`No ${TYPE_LABEL[letters.type]} template yet`}
          body="Approve and map a compatible template in Settings › Elections and letters before generating."
          action={{ label: 'Open letter templates', href: TEMPLATES_HREF }}
        />
      ) : (
        <>
          <div className={s.templateFields}>
            <div className={s.grow}>
              <Select
                label="Letter template"
                options={options}
                value={templateId ?? 'automatic'}
                onChange={(id) => {
                  onTemplate(id === 'automatic' ? null : id);
                }}
                isDisabled={!canGenerate || !canOverride}
              />
            </div>
            <div className={s.dateField}>
              <TextField
                label="Award date"
                type="date"
                value={dateDraft}
                onChange={onDate}
                // APP-130 D14: fixed from the first release; the letters that follow print it too.
                isDisabled={!yours || letters.released || letters.firstReleasedAt != null}
                description={
                  letters.firstReleasedAt != null
                    ? 'Printed in every letter. Fixed now: the first letters went out with it.'
                    : 'Printed in every letter.'
                }
              />
            </div>
          </div>
          {templateId !== null && (
            <TextField
              label="Template override reason"
              value={overrideReason}
              onChange={onOverrideReason}
              isRequired
              maxLength={500}
              isDisabled={!canGenerate}
              description="Recorded with the new letter revision. The selected version must fit the award type and amounts; review its signer."
            />
          )}
          {letters.lines.length > 1 && (
            <Select
              label="Generate for"
              value={targetLine ?? 'all'}
              options={[
                { id: 'all', label: 'Everyone on this award' },
                ...letters.lines.map((line) => ({ id: line.lineId, label: line.person.name })),
              ]}
              onChange={(id) => {
                onTargetLine(id === 'all' ? null : id);
              }}
              isDisabled={!canGenerate || dateChanged}
              description="A changed award date requires generating every person’s letter."
            />
          )}
          {!letters.released && (
            <div className={s.generateRow}>
              <Button
                variant="navy"
                icon={<Icon name="refresh" size={16} />}
                isPending={generating}
                isDisabled={
                  !letters.actions.generate ||
                  noDate ||
                  (templateId !== null &&
                    (overrideReason.trim() === '' ||
                      !options.some((option) => option.id === templateId))) ||
                  (targetLine !== null && dateChanged)
                }
                onPress={onGenerate}
              >
                {dateChanged && anyMade
                  ? 'Generate again with this date'
                  : anyMade
                    ? 'Generate again'
                    : 'Generate'}
              </Button>
              {/* The one message for an award without a date: at the field, never once per
                  person (QA-050). */}
              {canGenerate && noDate ? (
                <span className={o.muted}>{issueWords('award_date_required', '')}</span>
              ) : (
                canGenerate &&
                dateChanged &&
                anyMade && (
                  <span className={o.muted}>
                    Every letter is made again with {formatDate(dateDraft)}.
                  </span>
                )
              )}
            </div>
          )}
          {signerWords.length > 0 && !letters.released && (
            <Notice tone="problem">{signerWords.join(' ')}</Notice>
          )}
          {sampleNotice !== null && !letters.released && (
            <Notice tone="waiting">{sampleNotice}</Notice>
          )}
          {(letters.counts?.waiting ?? 0) > 0 && !letters.released && (
            <p className={o.muted}>
              Generate makes every letter it can now. Letters waiting on a missing letter are made
              once it’s ready, or from the letter chosen to stand in.
            </p>
          )}
        </>
      )}
      {/* A cash-only letter from this template would show empty option rows (QA-044). */}
      {chosen !== undefined &&
        showsEmptyOptions(chosen) &&
        letters.lines.some((l) => l.options === 0) &&
        !letters.released && <TemplateOptionsNote format={chosen.format} />}
      <p className={o.muted}>
        Templates are kept in{' '}
        <Link href={TEMPLATES_HREF} className={o.inlineLink}>
          Settings › Elections and letters
        </Link>
        .
      </p>
      <p className={o.muted}>
        {made ??
          // APP-244: while MyRewards makes them, there's nothing to generate yet.
          (letters.automatic?.status === 'waiting'
            ? 'Nothing made yet: MyRewards is making them now.'
            : letters.lines.length === 1
              ? 'Nothing generated yet. The letter fills in from the award once you generate it.'
              : `Nothing generated yet. ${plural(letters.lines.length, 'letter fills', 'letters fill')} in from the award once you generate.`)}
      </p>
      {error !== null && <Notice tone="problem">{error}</Notice>}
    </Card>
  );
}

function Download({ requestId, line }: { requestId: string; line: Line }): ReactElement | null {
  const letter = line.letter;
  if (letter === null) return null;
  if (!SCAN[letter.scanStatus].opens)
    return <span className={o.muted}>{SCAN[letter.scanStatus].label}</span>;
  return (
    <a
      className={o.inlineLink}
      href={letterFileHref(requestId, line.lineId)}
      download
      aria-label={`Download ${letter.fileName}, ${line.person.name}’s letter`}
    >
      Download {letter.fileName}
    </a>
  );
}

/**
 * Each person's letter: where it stands, what it needs, and the file. On an award for several
 * people, one person's missing, out-of-date or blocked letter can be made again on its own
 * (APP-067), leaving everyone else's as it is.
 */
function PeopleCard({
  letters,
  refused,
  onRemake,
  remaking,
  dateChanged,
  overrideReady,
  version,
  canEditCitation,
  previewing,
  onRead,
}: {
  letters: AwardLetters;
  refused: ReadonlyMap<string, readonly string[]>;
  onRemake: (lineId: string) => void;
  /** The person whose letter is being made again, if any. */
  remaking: string | null;
  /** A new award date goes in every letter, so one person's letter isn't made on its own. */
  dateChanged: boolean;
  overrideReady: boolean;
  version: number;
  canEditCitation: boolean;
  /** Whose letter the preview shows. */
  previewing: string | undefined;
  onRead: (lineId: string) => void;
}): ReactElement {
  return (
    <Card
      title={letters.lines.length === 1 ? 'The letter' : 'Each person’s letter'}
      help={{
        shows:
          'Each person’s letter: waiting on a template, made, being checked for viruses, ready to read, reviewed, marked Something’s wrong, out of date, or released.',
        canDo:
          'Read each letter in the preview and review it: encouraged, never required. A letter marked Something’s wrong is made again or cleared with a reason before it can go out.',
      }}
    >
      <ul className={s.letterList}>
        {letters.lines.map((line) => {
          const state = personStateOf(line);
          // A letter waiting on a template is explained once, by its gap card above.
          const problems = lineProblemItems(line, refused).filter(
            (p) => state !== 'waiting' || p.code !== 'letter_mapping_missing',
          );
          const first = firstName(line.person.name);
          return (
            <li key={line.lineId} className={s.letterRow}>
              <div className={s.letterHead}>
                <span className={o.strong}>{line.person.name}</span>
                <span className={o.muted}>{amountWords(line.cashCents, line.options)}</span>
                <StateChip line={line} />
              </div>
              {state === 'waiting' && (
                <p className={o.muted}>
                  Waits for the missing letter in the card above. Everyone else’s can go ahead.
                </p>
              )}
              {line.makeAgain === true && (
                <p className={o.muted}>
                  Made from a stand-in letter. The missing letter is ready now: make {first}’s
                  letter again from it, or let it go out as it is.
                </p>
              )}
              {line.review != null && <p className={o.muted}>{reviewWords(line.review)}</p>}
              {line.status === 'stale' && line.staleFields.length > 0 && (
                <p className={o.muted}>{staleWords(line.staleFields)}</p>
              )}
              {problems.length > 0 && (
                <ul className={s.issues} aria-label={`What ${line.person.name}’s letter needs`}>
                  {problems.map((p) =>
                    p.code === 'tax_estimates_missing' ? (
                      <li key={p.code}>
                        {taxMissingWords(line.person.name)}{' '}
                        <Link href={QUEUE_VIEW.tax.href} className={o.inlineLink}>
                          {TAX_TO_ENTER_LINK}
                        </Link>
                      </li>
                    ) : (
                      <li key={p.code}>{p.words}</li>
                    ),
                  )}
                </ul>
              )}
              {/* APP-246: what to know that stops nothing, such as no employee number. */}
              {lineNotes(line).map((words) => (
                <p key={words} className={o.muted}>
                  {words}
                </p>
              ))}
              {line.selectedTemplate ? (
                <p className={o.muted}>
                  Mapped template: {line.selectedTemplate.name}{' '}
                  {shownVersion(line.selectedTemplate)}
                </p>
              ) : (
                isBuiltInSample(line) && (
                  <p className={o.muted}>
                    Letter:{' '}
                    {builtInSampleName(
                      line.letter?.builtInSample?.version ?? line.builtInSample?.version ?? 1,
                    )}
                    , marked as a sample
                  </p>
                )
              )}
              {/* APP-201: a Spot letter's citation is the recipient's own; every other award's
                  wording is one for everyone, in the Letter wording card. */}
              {canEditCitation && letters.type === 'SPOT' && (
                <PublicCitation
                  key={`${line.lineId}:${line.lineUpdatedAt ?? ''}`}
                  letters={letters}
                  line={line}
                  version={version}
                />
              )}
              <Download requestId={letters.requestId} line={line} />
              {letters.lines.length > 1 && line.letter !== null && previewing !== line.lineId && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Icon name="eye" size={14} />}
                  onPress={() => {
                    onRead(line.lineId);
                  }}
                >
                  Read {first}’s letter
                </Button>
              )}
              {letters.lines.length > 1 &&
                letters.actions.generate &&
                !dateChanged &&
                remakeable(line) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Icon name="refresh" size={14} />}
                    isPending={remaking === line.lineId}
                    isDisabled={!overrideReady || (remaking !== null && remaking !== line.lineId)}
                    onPress={() => {
                      onRemake(line.lineId);
                    }}
                  >
                    {remakeLabel(line)}
                  </Button>
                )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * The finished letter in a frame once it's made, current and through its virus check (APP-067),
 * then the facts it fills in, highlighted, with the estimated-tax table for a PAA with cash.
 */
function PreviewCard({
  letters,
  line,
  onLine,
  onReviewed,
  onRemake,
  remaking,
}: {
  letters: AwardLetters;
  line: Line | undefined;
  onLine: (lineId: string) => void;
  onReviewed: (next: AwardLetters, said: string) => Promise<void>;
  onRemake: (lineId: string) => void;
  remaking: string | null;
}): ReactElement {
  const help = {
    shows:
      'The letter exactly as it goes out, and what it fills in from the award, highlighted: the date, the person, the amounts, the vesting, the signer and, for a PAA with cash, the estimated tax for each choice.',
    canDo:
      'Read it, check it against the award and review it: encouraged, never required. If it doesn’t appear in your browser, download it to read the finished file.',
  };
  const picker =
    letters.lines.length > 1 ? (
      <Select
        label="Whose letter"
        options={letters.lines.map((l) => ({ id: l.lineId, label: l.person.name }))}
        value={line?.lineId ?? null}
        onChange={(id) => {
          if (id !== null) onLine(id);
        }}
      />
    ) : null;
  if (line === undefined || (line.letter === null && line.mergeFields === null))
    return (
      <Card title="Preview" help={help} helpAnchor="letters-preview">
        {picker}
        <EmptyState
          variant="first-day"
          art="letter"
          headline="Nothing to read yet"
          body={
            line === undefined
              ? 'When an award passes its check, its letter shows here with every filled-in fact highlighted, so you can read it before it goes out.'
              : `${firstName(line.person.name)}’s letter shows here once it can be made, with every filled-in fact highlighted. What it needs is listed with the letter.`
          }
          action={{ label: 'Back to the queue', href: QUEUE_VIEW.queue.href }}
        />
      </Card>
    );
  const facts = letterFacts(line.mergeFields);
  const tax = taxRows(line.mergeFields);
  const notYet = line.letter === null || line.status === 'stale' || line.status === 'blocked';
  // Only a current letter that has passed its virus check is displayed; the server checks again.
  const framed =
    line.letter !== null && !notYet && SCAN[line.letter.scanStatus].opens ? line.letter : null;
  return (
    <Card
      title="Preview"
      helpAnchor="letters-preview"
      help={help}
      aside={
        <span className={s.legend}>
          <span className={s.fill}>Highlighted</span> = filled from the award
        </span>
      }
    >
      {picker}
      {notYet && (
        <p className={o.muted}>
          {line.letter === null
            ? `What ${firstName(line.person.name)}’s letter will say once it’s generated.`
            : `What ${firstName(line.person.name)}’s letter will say when it’s generated again.`}
        </p>
      )}
      {framed !== null && (
        <PdfPreview
          key={`${line.lineId}-${framed.documentId}`}
          title={`${line.person.name}’s letter, ${framed.fileName}`}
          src={letterInlineHref(letters.requestId, line.lineId)}
          downloadHref={letterFileHref(letters.requestId, line.lineId)}
          downloadName={framed.fileName}
        />
      )}
      <article className={s.paper} aria-label={`${line.person.name}’s letter`}>
        {framed !== null && <h3 className={s.factsTitle}>What it fills in</h3>}
        <dl className={s.paperFacts}>
          {facts.map((f) => (
            <div key={f.label}>
              <dt>{f.label}</dt>
              <dd>
                <span className={s.fill}>{f.value}</span>
              </dd>
            </div>
          ))}
        </dl>
        {tax.length > 0 && (
          <section className={s.estimateBox} aria-labelledby={`tax-${line.lineId}`}>
            <div className={s.estimateHead}>
              <h3 id={`tax-${line.lineId}`} className={s.estimateTitle}>
                {line.mergeFields?.['estimateLabel'] ?? 'Estimated tax for each choice'}
              </h3>
              <StatusPill size="sm" tone="waiting" label="Estimate" />
            </div>
            <table className={s.estimateTable}>
              <caption className="visually-hidden">
                Estimated tax for each choice in {line.person.name}’s letter
              </caption>
              <thead>
                <tr>
                  <th scope="col">Choice</th>
                  <th scope="col">Estimated tax</th>
                </tr>
              </thead>
              <tbody>
                {tax.map((row) => (
                  <tr key={row.choice}>
                    <th scope="row">
                      <span className={s.fill}>{row.choice}</span>
                    </th>
                    <td>
                      <span className={s.fill}>{row.estimate}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className={o.muted}>Estimates from the check. Payroll sets the final tax.</p>
          </section>
        )}
      </article>
      <div className={s.downloadRow}>
        <Download requestId={letters.requestId} line={line} />
      </div>
      {/* APP-246: said where the letter is checked too, since its check has no number to show. */}
      {lineNotes(line).map((words) => (
        <p key={words} className={o.muted}>
          {words}
        </p>
      ))}
      <CheckAgainstAward key={`check-${line.lineId}`} line={line} />
      <ReviewPanel
        key={`review-${line.lineId}-${line.letter?.documentId ?? ''}`}
        letters={letters}
        line={line}
        onReviewed={onReviewed}
        onRemake={onRemake}
        remaking={remaking === line.lineId}
      />
    </Card>
  );
}

/**
 * "Someone else" (APP-057): anyone active in the directory, found by name. The reason for the
 * change is asked for at release, and the server checks the person again.
 */
function SomeoneElse({
  line,
  choices,
  onPick,
  disabled,
}: {
  line: Line;
  choices: readonly HolderChoice[];
  onPick: (person: { id: string; name: string }) => void;
  disabled: boolean;
}): ReactElement {
  return (
    <PersonPicker
      label={`Someone else for ${firstName(line.person.name)}’s letter`}
      description="Anyone active in the directory. The change and your reason go in the award’s history."
      exclude={new Set(choices.map((c) => c.id))}
      onPick={(person) => {
        onPick({ id: person.id, name: person.name });
      }}
      isDisabled={disabled}
    />
  );
}

function HolderPicker({
  letters,
  line,
  choices,
  value,
  onChange,
  onPickOther,
  disabled,
}: {
  letters: AwardLetters;
  line: Line;
  choices: readonly HolderChoice[];
  value: string | null;
  onChange: (id: string) => void;
  onPickOther: (person: { id: string; name: string }) => void;
  disabled: boolean;
}): ReactElement {
  const other = (
    <SomeoneElse line={line} choices={choices} onPick={onPickOther} disabled={disabled} />
  );
  if (letters.lines.length === 1)
    return (
      <>
        <RadioGroup
          aria-label="Letter goes to"
          value={value}
          onChange={onChange}
          isDisabled={disabled}
          className={s.holderGroup}
        >
          {choices.map((c) => (
            <RadioField key={c.id} value={c.id}>
              <RadioButton className={s.holderCard}>
                <span className={s.radioDot} aria-hidden="true" />
                <span className={s.holderText}>
                  <span className={s.holderName}>{c.name}</span>
                  <span className={s.holderDetail}>{c.detail}</span>
                </span>
              </RadioButton>
            </RadioField>
          ))}
        </RadioGroup>
        {other}
      </>
    );
  return (
    <div className={o.stack}>
      <Select
        label={`${line.person.name}’s letter goes to`}
        options={choices.map((c) => ({ id: c.id, label: `${c.name} · ${c.detail}` }))}
        value={value}
        onChange={(id) => {
          if (id !== null) onChange(id);
        }}
        isDisabled={disabled}
      />
      {other}
    </div>
  );
}

/**
 * After Release: the claim has ended and nothing is left to do here, so the summary takes focus
 * and offers what's next (QA-050): the oldest award still waiting for a claim, or the queue.
 */
function NextInQueue({ awardId }: { awardId: string }): ReactElement {
  const queue = useQuery(queueQuery('queue'));
  const next = queue.data === undefined ? undefined : nextInQueue(queue.data.rows, awardId);
  if (next === undefined)
    return (
      <div className={s.nextUp}>
        {queue.data !== undefined && (
          <p className={o.muted}>Nothing else is waiting for a claim.</p>
        )}
        <ButtonLink href={QUEUE_VIEW.queue.href} variant="navy" block>
          Back to the queue
        </ButtonLink>
      </div>
    );
  return (
    <div className={s.nextUp}>
      <p className={o.fieldLabel}>Next in your queue</p>
      <ButtonLink
        href={rowHref(next)}
        variant="navy"
        block
        aria-label={`Next in your queue: ${next.award.displayId} · ${next.subject}`}
      >
        {next.award.displayId} · {next.subject}
      </ButtonLink>
      <p className={o.muted}>
        {TYPE_LABEL[next.type]} · {next.pool}
      </p>
      <ButtonLink href={QUEUE_VIEW.queue.href} variant="ghost" block>
        Back to the queue
      </ButtonLink>
    </div>
  );
}

function ReleaseCard({
  letters,
  yours,
  chosen,
  onChoose,
  dateDraft,
  focusReleased,
  onReleased,
  onRefused,
}: {
  letters: AwardLetters;
  yours: boolean;
  chosen: ReadonlyMap<string, string>;
  onChoose: (lineId: string, holderId: string) => void;
  dateDraft: string;
  /** Counts releases made on this page: each one moves focus to the summary. */
  focusReleased: number;
  onReleased: (letters: AwardLetters) => Promise<void>;
  onRefused: (error: unknown) => Promise<void>;
}): ReactElement {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const releasedHeading = useRef<HTMLHeadingElement>(null);
  const progressHeading = useRef<HTMLHeadingElement>(null);
  // The confirm dialog closes as this card turns into its summary, and the button it would hand
  // focus back to is gone or off: focus the summary instead, never the page body (QA-050). One
  // tick later, once the dialog has let go. Opening an award that's already released doesn't
  // move it. After some of the letters (APP-130), focus how many are out.
  useEffect(() => {
    if (focusReleased === 0) return;
    const timer = window.setTimeout(() => {
      (letters.released ? releasedHeading : progressHeading).current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [focusReleased, letters.released]);
  // The type's default letter holder is a setting: who changed it last, with its history.
  const holderSetting = useQuery(settingQuery('letters.defaultRecipient', letters.type));
  // People picked by name ("Someone else") for each letter, offered beside the server's choices.
  const [picked, setPicked] = useState<ReadonlyMap<string, { id: string; name: string }[]>>(
    new Map(),
  );
  const pickOther = (lineId: string, person: { id: string; name: string }): void => {
    setPicked((now) => new Map(now).set(lineId, [...(now.get(lineId) ?? []), person]));
    onChoose(lineId, person.id);
  };
  const choices = new Map(
    letters.lines.map(
      (l) => [l.lineId, holderChoices(letters, l, picked.get(l.lineId) ?? [])] as const,
    ),
  );
  const holders = letters.lines.map((l) => holderOf(l, chosen, choices.get(l.lineId) ?? []));
  const [only] = letters.lines;
  const batch = releaseBatch(letters);
  const help = {
    shows:
      'Who each letter goes to, and how many are out. The letter holder hands the letter out; the employee sees the award from the award date, set with the template.',
    canDo:
      'Release the letters that are ready now; the rest follow later, to the same holder. Reviewing first is encouraged, never required. The award date stays as it is once the first letters are out.',
  };

  // Replacing a holder the line already had needs a reason, audited on the line (APP-056).
  const replaced = replacedHolders(letters, chosen, holders);
  const release = useMutation({
    mutationFn: (reason: string) => {
      const recipients = changedHolders(letters, chosen);
      return releaseLetters(letters.requestId, {
        awardDate: letters.awardDate ?? dateDraft,
        // Exactly the letters confirmed: one that turns ready meanwhile waits for the next.
        lineIds: batch.now.map((l) => l.lineId),
        ...(recipients.length > 0 && { recipients }),
        ...(replaced.length > 0 && reason.trim() !== '' && { reason: reason.trim() }),
      });
    },
  });

  if (letters.released)
    return (
      <Card title="Released" help={help} accent="gold" headingRef={releasedHeading}>
        <Notice tone="done">
          {letters.lines.length === 1 ? 'The letter is' : 'Every letter is'} with its holder.{' '}
          {letters.awardDate === null
            ? 'Each person sees the award on its award date.'
            : `${only !== undefined && letters.lines.length === 1 ? firstName(only.person.name) : 'Each person'} sees the award on ${formatDate(letters.awardDate)}.`}
        </Notice>
        <dl className={o.summary}>
          {letters.lines.map((l) => (
            <div key={l.lineId} className={o.summaryRow}>
              <dt>{l.person.name}</dt>
              <dd>
                {l.recipient === null ? 'Released' : `With ${l.recipient.name}`}
                {l.releasedAt !== null && ` · ${formatShortDate(l.releasedAt)}`}
              </dd>
            </div>
          ))}
        </dl>
        <NextInQueue awardId={letters.requestId} />
      </Card>
    );

  const blockers = releaseBlockers(letters, dateDraft, holders, yours);
  const canRelease = blockers.length === 0 && letters.actions.release;
  const summary = releaseSummary(letters, holders);
  const first = only === undefined ? 'The employee' : firstName(only.person.name);
  const toGo = letters.lines.filter((l) => l.releasedAt === null);
  const following = followingWords(letters, holders);
  const allAtOnce = batch.following.length === 0 && batch.released.length === 0;
  const [goingOnly] = batch.now;
  const title = allAtOnce
    ? letters.lines.length === 1
      ? `Release ${first}’s letter?`
      : 'Release every letter?'
    : batch.now.length === 1 && goingOnly !== undefined
      ? `Release ${firstName(goingOnly.person.name)}’s letter now?`
      : `Release ${plural(batch.now.length, 'letter', 'letters')} now?`;
  const share = Math.round((batch.released.length / Math.max(letters.lines.length, 1)) * 100);

  return (
    <Card title="Release" help={help} helpAnchor="letters-release" accent="gold">
      {batch.released.length > 0 && (
        <section className={lt.progress} aria-labelledby="letters-progress">
          <h3 id="letters-progress" ref={progressHeading} tabIndex={-1} className={o.fieldLabel}>
            {releasedWords(letters)}
          </h3>
          <div className={lt.meter} aria-hidden="true">
            <div className={lt.meterFill} style={{ width: `${String(share)}%` }} />
          </div>
          <dl className={o.summary}>
            {batch.released.map((line) => (
              <div key={line.lineId} className={o.summaryRow}>
                <dt>{line.person.name}</dt>
                <dd>
                  {line.recipient === null ? 'Released' : `With ${line.recipient.name}`}
                  {line.releasedAt !== null && ` · ${formatShortDate(line.releasedAt)}`}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      {toGo.length === 1 && letters.lines.length === 1 && only !== undefined ? (
        <fieldset className={s.fieldset}>
          <legend className={o.fieldLabel}>Letter goes to</legend>
          <HolderPicker
            letters={letters}
            line={only}
            choices={choices.get(only.lineId) ?? []}
            value={holders[0]?.id ?? null}
            onChange={(id) => {
              onChoose(only.lineId, id);
            }}
            onPickOther={(person) => {
              pickOther(only.lineId, person);
            }}
            disabled={!yours}
          />
        </fieldset>
      ) : (
        <div className={o.stack}>
          {letters.lines.map((line, i) =>
            line.releasedAt !== null ? null : (
              <HolderPicker
                key={line.lineId}
                letters={letters}
                line={line}
                choices={choices.get(line.lineId) ?? []}
                value={holders[i]?.id ?? null}
                onChange={(id) => {
                  onChoose(line.lineId, id);
                }}
                onPickOther={(person) => {
                  pickOther(line.lineId, person);
                }}
                disabled={!yours}
              />
            ),
          )}
        </div>
      )}
      {following.length > 0 && (
        <section aria-labelledby="letters-following">
          <h3 id="letters-following" className={o.fieldLabel}>
            Following later, to the same holder
          </h3>
          <ul className={lt.followList}>
            {following.map((words) => (
              <li key={words}>{words}</li>
            ))}
          </ul>
        </section>
      )}
      <p className={o.muted}>
        {defaultHolderWords(letters)}.
        {holderSetting.data !== undefined && (
          <>
            {' '}
            <SettingChange
              view={holderSetting.data}
              title={`Who gets ${TYPE_SHORT[letters.type]} letters`}
              subject="History · Elections and letters"
              words={recipientWords}
            />
          </>
        )}
      </p>
      {/* The award date is set with the template, beside Generate (QA-050); here it's a fact. */}
      {letters.awardDate !== null && (
        <p className={o.muted}>
          Award date {formatDate(letters.awardDate)}.{' '}
          {letters.lines.length === 1
            ? `Hidden from ${first} until then, unless the letter holder releases it early.`
            : 'Hidden from each person until then, unless their letter holder releases it early.'}
        </p>
      )}
      <Button
        variant="gold"
        block
        icon={<Icon name="send" size={16} />}
        isDisabled={!canRelease}
        onPress={() => {
          setError(null);
          setConfirming(true);
        }}
      >
        {releaseLabel(letters)}
      </Button>
      {blockers.length > 0 ? (
        <ul className={s.blockers} aria-label="Before you can release">
          {blockers.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      ) : !letters.actions.release ? (
        <p className={o.muted}>These letters aren’t ready to release yet. Reload to check.</p>
      ) : (
        <p className={o.muted}>{releaseSentence(letters, holders)}</p>
      )}
      <ConfirmDialog
        isOpen={confirming}
        onOpenChange={setConfirming}
        title={title}
        intro={
          replaced.length === 0
            ? summary.intro
            : `${summary.intro} You changed who holds ${replaced.length === 1 ? 'a letter' : 'some letters'}, so say why; the reason goes in the award’s history.`
        }
        summary={[
          ...summary.rows,
          ...replaced.map((r) => ({
            label: `${firstName(r.person)}’s letter holder`,
            value: `${r.to}, instead of ${r.from}`,
          })),
        ]}
        {...(replaced.length === 0
          ? { withoutReason: true }
          : { reasonLabel: 'Why does the letter go to someone else?' })}
        confirmLabel={releaseLabel(letters)}
        confirmVariant="gold"
        error={error}
        onConfirm={async (reason) => {
          try {
            const next = await release.mutateAsync(reason);
            await onReleased(next);
            return true;
          } catch (e) {
            setError(letterProblemWords(e));
            await onRefused(e);
            return false;
          }
        }}
      />
    </Card>
  );
}

function MergeFieldsCard({
  letters,
  line,
}: {
  letters: AwardLetters;
  line: Line | undefined;
}): ReactElement {
  return (
    <Card
      title="Merge fields used"
      tone="dark"
      compact
      titleSize="sm"
      help={{
        shows: 'What the letter fills in and where each fact comes from.',
        canDo:
          'Fix a wrong fact where it lives (the directory, the check or the template), then generate again.',
      }}
    >
      <dl className={o.about}>
        {mergeSummary(letters, line).map(([label, value]) => (
          <div key={label} className={o.aboutRow}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <p className={o.onDarkNote}>
        {letters.type === 'PAA'
          ? 'The ADP screenshot stays on the record and is not in the letter.'
          : 'Supporting files stay on the record and are not in the letter.'}
      </p>
      <Link href={`/awards/${letters.requestId}`} className={o.onDarkLink}>
        Award record and history
      </Link>
    </Card>
  );
}

/**
 * SA3: one award's letters. Generate every person's letter from a template, read what each one
 * says, choose who holds it and the award date, then release them all together (S5 → S6).
 */
export function LettersPage(): ReactElement {
  const id = useAwardId();
  const toast = useToast();
  const queryClient = useQueryClient();
  const claimChanged = useClaimChanged();
  const { data: me } = useSuspenseQuery(meQuery);
  const { data: letters } = useSuspenseQuery({
    ...lettersQuery(id),
    // A letter in its virus check, or MyRewards still making them (APP-244), comes in on its own.
    refetchInterval: (q) =>
      q.state.data?.lines.some((l) => l.status === 'checking') === true ||
      q.state.data?.automatic?.status === 'waiting'
        ? CHECKING_POLL_MS
        : false,
  });
  const { data: check } = useSuspenseQuery(checkQuery(id));
  const meId = me.person.id;
  const yours = check.claimedBy?.id === meId && letters.actions.generate;

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const effectiveTemplateId = letters.actions.overrideTemplate === false ? null : templateId;
  const [targetLine, setTargetLine] = useState<string | null>(null);
  const [chosen, setChosen] = useState<ReadonlyMap<string, string>>(new Map());
  const [dateDraft, setDateDraft] = useState(letters.awardDate ?? '');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [refused, setRefused] = useState<ReadonlyMap<string, readonly string[]>>(new Map());
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [releasedHere, setReleasedHere] = useState(0);
  const line = letters.lines.find((l) => l.lineId === previewId) ?? letters.lines[0];
  const displayId = letters.displayId ?? 'This award';
  // APP-244: what MyRewards' own run met, beside what a refused Generate said, by person.
  const shownRefused = mergedIssues(automaticIssues(letters), refused);
  const autoNotice = automaticNotice(letters);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      claimChanged(id),
      queryClient.invalidateQueries({ queryKey: letterKeys.one(id) }),
    ]);
  };

  // Every letter, every letter with a new award date, or one person's letter (APP-067).
  const generate = useMutation({
    mutationFn: (ask: { awardDate?: string; lineId?: string }) =>
      generateLetters(id, {
        ...(effectiveTemplateId !== null && {
          templateId: effectiveTemplateId,
          overrideReason: overrideReason.trim(),
        }),
        ...(ask.awardDate !== undefined && { awardDate: ask.awardDate }),
        ...(ask.lineId !== undefined && { lineIds: [ask.lineId] }),
      }),
    onSuccess: async (next, ask) => {
      queryClient.setQueryData(letterKeys.one(id), next);
      setGenerateError(null);
      // D22: letters held back for a doubled word say why on their rows; the rest were made.
      const held = heldIssues(next);
      setRefused(held);
      if (next.awardDate !== null) setDateDraft(next.awardDate);
      await refresh();
      const person = next.lines.find((l) => l.lineId === ask.lineId)?.person.name;
      toast.show({
        tone: 'done',
        title:
          person !== undefined
            ? `${firstName(person)}’s letter generated · ${displayId}`
            : `${next.lines.length === 1 ? 'Letter' : 'Letters'} generated · ${displayId}`,
        body:
          held.size > 0
            ? `${plural(held.size, 'letter was', 'letters were')} held back: what each needs is listed with it.`
            : next.lines.some((l) => l.status === 'checking')
              ? 'Each letter is checked for viruses before it can go out. That usually takes a minute.'
              : 'Read and review each one, then release the ones that are ready.',
      });
    },
    onError: async (e) => {
      const issues = refusedIssues(e);
      // What the whole award needs is said once, at its control, not as a general error too.
      setGenerateError(onlyAwardIssues(issues) ? null : letterProblemWords(e));
      setRefused(issues);
      await refresh();
    },
  });

  // A review or a fallback answers with the new view: show it, refresh what depends on it, say so.
  const changed = async (next: AwardLetters, said: string): Promise<void> => {
    queryClient.setQueryData(letterKeys.one(id), next);
    setRefused(new Map());
    await refresh();
    toast.show({ tone: 'done', title: `${said} · ${displayId}` });
  };

  const awardDateWords =
    letters.awardDate === null ? 'Not set' : formatShortDate(letters.awardDate);
  const ready = letters.lines.filter((l) => l.status === 'ready' || l.status === 'released').length;
  // APP-101: the tax waits on the letters, not the check, so it's counted on its own.
  const checkDone =
    check.completed || check.checklist.every((item) => item.id === 'tax' || item.status !== 'open');
  const taxLeft = letters.lines.filter((l) =>
    l.issues.some((i) => i.code === 'tax_estimates_missing'),
  ).length;
  const [only] = letters.lines;

  const releasedCount = letters.lines.filter((l) => l.releasedAt !== null).length;
  const sub = letters.released
    ? `Released. Each letter is with its holder, and ${letters.lines.length === 1 && only !== undefined ? firstName(only.person.name) : 'each person'} sees the award on the award date.`
    : releasedCount > 0
      ? `${releasedWords(letters)}. The rest follow to the same holder once they’re made and released.`
      : letters.automatic != null && letters.automatic.status !== 'not_made'
        ? 'MyRewards makes these letters when the check is done. Read and review each one, change the wording or template if it needs it, then release the ready ones to their holders.'
        : 'Generate from the template, read and review each letter, then release the ready ones to their holders.';

  const toMake = letters.lines.length - ready;
  const dateChanged = dateDraft !== '' && dateDraft !== letters.awardDate;

  return (
    <>
      <Hero
        eyebrow={`${displayId} · Letters`}
        title={lettersTitle(letters)}
        sub={sub}
        stats={[
          {
            label: scoped(displayId, 'check'),
            value: checkDone ? 'Done' : 'Open',
            meta: !checkDone
              ? 'Finish the check first'
              : taxLeft > 0
                ? `Checked · tax to enter for ${plural(taxLeft, 'person', 'people')}`
                : 'Checked, with each person’s tax',
          },
          releasedCount > 0 && !letters.released
            ? {
                label: scoped(displayId, 'letters released'),
                value: `${String(releasedCount)} of ${String(letters.lines.length)}`,
                meta: `${plural(letters.lines.length - releasedCount, 'letter follows', 'letters follow')} to the same holder`,
              }
            : {
                label: scoped(displayId, 'letters ready'),
                value: `${String(ready)} of ${String(letters.lines.length)}`,
                meta: letters.released
                  ? 'Released'
                  : toMake === 0
                    ? 'Ready to release'
                    : `${plural(toMake, 'letter', 'letters')} still to make or check`,
              },
          {
            label: scoped(displayId, 'award date'),
            value: awardDateWords,
            meta: 'When the employee sees it',
            tone: 'gold',
          },
        ]}
        titleTestId="letters-title"
      />
      <ManageTabs current="letters" />
      <PageBody>
        <div className={o.stack}>
          <BackLink href={`/queue/${id}`}>The check</BackLink>
          <ClaimState check={check} letters={letters} me={me} />
          {autoNotice !== null && <Notice tone={autoNotice.tone}>{autoNotice.words}</Notice>}
          <TwoColumns
            sideLabel="Release and merge fields"
            main={
              <div className={o.stack}>
                {(letters.gaps ?? []).map((gap) => (
                  <GapCard key={gap.cellKey} letters={letters} gap={gap} onSaved={changed} />
                ))}
                <LetterWordingCard
                  letters={letters}
                  version={letters.version ?? check.award.version}
                  canEdit={yours && !letters.released && me.preview === null}
                  onSaved={changed}
                />
                <TemplateCard
                  letters={letters}
                  templateId={effectiveTemplateId}
                  onTemplate={(id) => {
                    setTemplateId(id);
                    setOverrideReason('');
                  }}
                  overrideReason={overrideReason}
                  onOverrideReason={setOverrideReason}
                  targetLine={targetLine}
                  onTargetLine={(id) => {
                    setTargetLine(id);
                    setTemplateId(null);
                    setOverrideReason('');
                  }}
                  dateDraft={dateDraft}
                  onDate={(date) => {
                    setDateDraft(date);
                    if (targetLine !== null) {
                      setTargetLine(null);
                      setTemplateId(null);
                      setOverrideReason('');
                    }
                  }}
                  yours={yours}
                  onGenerate={() => {
                    setGenerateError(null);
                    // A new award date, or the first one, is saved with the letters made from it.
                    generate.mutate(
                      dateChanged
                        ? { awardDate: dateDraft }
                        : targetLine !== null
                          ? { lineId: targetLine }
                          : {},
                    );
                  }}
                  generating={generate.isPending && generate.variables.lineId === undefined}
                  error={generateError}
                  awardIssues={awardIssueCodes(letters, shownRefused)}
                />
                <PeopleCard
                  letters={letters}
                  refused={shownRefused}
                  onRemake={(lineId) => {
                    setGenerateError(null);
                    if (
                      effectiveTemplateId !== null &&
                      !templateOptions(letters, lineId).some((t) => t.id === effectiveTemplateId)
                    ) {
                      setGenerateError(
                        'Choose a compatible template for this person using Generate for.',
                      );
                      return;
                    }
                    generate.mutate({ lineId });
                  }}
                  remaking={generate.isPending ? (generate.variables.lineId ?? null) : null}
                  dateChanged={dateChanged}
                  overrideReady={effectiveTemplateId === null || overrideReason.trim() !== ''}
                  version={letters.version ?? check.award.version}
                  canEditCitation={yours && !letters.released && me.preview === null}
                  previewing={line?.lineId}
                  onRead={setPreviewId}
                />
                <PreviewCard
                  letters={letters}
                  line={line}
                  onLine={setPreviewId}
                  onReviewed={changed}
                  onRemake={(lineId) => {
                    setGenerateError(null);
                    generate.mutate({ lineId });
                  }}
                  remaking={generate.isPending ? (generate.variables.lineId ?? null) : null}
                />
              </div>
            }
            side={
              <div className={o.stack}>
                <ReleaseCard
                  letters={letters}
                  yours={yours}
                  chosen={chosen}
                  onChoose={(lineId, holderId) => {
                    setChosen((current) => new Map(current).set(lineId, holderId));
                  }}
                  dateDraft={dateDraft}
                  focusReleased={releasedHere}
                  onReleased={async (next) => {
                    setReleasedHere((n) => n + 1);
                    queryClient.setQueryData(letterKeys.one(id), next);
                    setRefused(new Map());
                    setChosen(new Map());
                    await refresh();
                    const holders = [
                      ...new Set(
                        next.lines.flatMap((l) => (l.recipient ? [l.recipient.name] : [])),
                      ),
                    ];
                    toast.show({
                      tone: 'done',
                      title: next.released
                        ? `${next.lines.length === 1 ? 'Letter' : 'Letters'} released · ${displayId}`
                        : `${releasedWords(next)} · ${displayId}`,
                      body: next.released
                        ? `${holders.length === 0 ? 'Each holder' : holders.join(', ')} ${holders.length === 1 ? 'has' : 'have'} ${next.lines.length === 1 ? 'it' : 'them'} now. The award shows in My rewards on its award date.`
                        : 'Their holders have them now. The award is back in the queue at Letters for the rest, which follow to the same holder.',
                    });
                  }}
                  onRefused={async (e) => {
                    setRefused(refusedIssues(e));
                    await refresh();
                  }}
                />
                <MergeFieldsCard letters={letters} line={line} />
              </div>
            }
          />
        </div>
      </PageBody>
    </>
  );
}
